// src/components/BranchManagerUserManagement.js

import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../api/axiosConfig";
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Button,
  Tooltip,
} from "@mui/material";
import { FiSearch, FiArrowLeft, FiClock, FiDownload } from "react-icons/fi";
import "./BranchManagerUserManagement.css";

const formatDateForInput = (date) => {
  if (!date || !(date instanceof Date) || isNaN(date)) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatNotificationDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const getMetric = (member, key) => {
  return member[key] || 0;
};

const BranchManagerUserManagement = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [allUsers, setAllUsers] = useState([]);
  const [selectedTeamLead, setSelectedTeamLead] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const [dateRange, setDateRange] = useState({
    startDate: (() => {
      const d = new Date();
      d.setDate(d.getDate() - 29);
      return d;
    })(),
    endDate: new Date(),
  });

  const fetchBranchUsers = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.get("/api/users/users-by-branch-manager/me", {
        params: {
          startDate: formatDateForInput(dateRange.startDate),
          endDate: formatDateForInput(dateRange.endDate),
        },
      });
      const users = response.data || [];
      setAllUsers(users);

      // --- THIS IS THE CRITICAL FIX ---
      // Check for navigation state *after* the data has been fetched.
      if (location.state?.preSelectedTeamLead) {
        const preSelected = users.find(
          (user) => user.userId === location.state.preSelectedTeamLead.userId
        );
        if (preSelected) {
          setSelectedTeamLead(preSelected);
        }
        // Clear the state to prevent it from re-triggering on refresh
        navigate(location.pathname, { replace: true, state: {} });
      }
    } catch (err) {
      setError("Failed to fetch user data. Please try again later.");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [dateRange, location, navigate]);

  useEffect(() => {
    fetchBranchUsers();
  }, [fetchBranchUsers]);

  const teamLeads = allUsers.filter(
    (u) =>
      u.role === "team_lead" &&
      (u.name || u.username).toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Direct RMs are RMs that report directly to the branch manager (no team lead)
  const directRelationshipManagers = allUsers.filter(
    (u) =>
      u.role === "relationship_manager" &&
      (!u.teamLeadName || u.teamLeadName === null || u.teamLeadName === "") &&
      (u.name || u.username).toLowerCase().includes(searchTerm.toLowerCase())
  );

  const relationshipManagers = selectedTeamLead
    ? allUsers.filter(
        (u) =>
          u.role === "relationship_manager" &&
          u.teamLeadName === selectedTeamLead.name &&
          (u.name || u.username)
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
      )
    : [];

  const handleBackToTLs = () => {
    setSelectedTeamLead(null);
    setSearchTerm("");
  };

  const downloadCSV = () => {
    let data = [];
    let fileName = "";
    let headers = [];

    if (selectedTeamLead) {
      // Download RMs under selected team lead
      data = relationshipManagers;
      fileName = `relationship_managers_under_${selectedTeamLead.name.replace(/\s+/g, '_')}`;
      headers = [
        "RM Name",
        "Status",
        "Branch Manager",
        "Zonal Head",
        "Director",
        "Calls",
        "F2F",
        "Appt. Made",
        "Appt. Kept",
        "Fact Find",
        "Reviews",
        "Social",
        "HIW",
        "Referrals",
        "Closing Int.",
        "Sales Close",
      ];
    } else {
      // Download all visible data (team leads + direct RMs)
      data = [...teamLeads, ...directRelationshipManagers];
      fileName = "branch_team_overview";
      headers = [
        "Name",
        "Role",
        "Status",
        "Branch Manager",
        "Zonal Head",
        "Director",
        "Calls",
        "F2F",
        "Appt. Made",
        "Appt. Kept",
        "Fact Find",
        "Reviews",
        "Social",
        "HIW",
        "Referrals",
        "Closing Int.",
        "Sales Close",
      ];
    }

    if (data.length === 0) return;

    const csvContent = [
      headers.join(","),
      ...data.map((user) => {
        const isTeamLead = user.role === "team_lead";
        const isDirectRM = user.role === "relationship_manager" && (!user.teamLeadName || user.teamLeadName === null || user.teamLeadName === "");

        if (selectedTeamLead) {
          // For team lead RM view: RM Name, Status, Branch Manager, Zonal Head, Director, metrics...
          return [
            `"${user.name}"`,
            user.leaveStatus ? "On Leave" : "Active",
            `"${user.branchManagerName || "N/A"}"`,
            `"${user.zonalHeadName || "N/A"}"`,
            `"${user.directorName || "N/A"}"`,
            getMetric(user, "telephoneSalesCalls"),
            getMetric(user, "faceToFace"),
            getMetric(user, "appointmentMade"),
            getMetric(user, "appointmentKept"),
            getMetric(user, "factFind"),
            getMetric(user, "serviceReviews"),
            getMetric(user, "socialCalls"),
            getMetric(user, "highIncomeWealthy"),
            getMetric(user, "referrals"),
            getMetric(user, "closingInterview"),
            getMetric(user, "salesClose"),
          ].join(",");
        } else {
          // For overview: Name, Role, Status, Branch Manager, Zonal Head, Director, metrics...
          return [
            `"${user.name}"`,
            isTeamLead ? "Team Lead" : "Direct RM",
            user.leaveStatus ? "On Leave" : "Active",
            `"${user.branchManagerName || "N/A"}"`,
            `"${user.zonalHeadName || "N/A"}"`,
            `"${user.directorName || "N/A"}"`,
            getMetric(user, "telephoneSalesCalls"),
            getMetric(user, "faceToFace"),
            getMetric(user, "appointmentMade"),
            getMetric(user, "appointmentKept"),
            getMetric(user, "factFind"),
            getMetric(user, "serviceReviews"),
            getMetric(user, "socialCalls"),
            getMetric(user, "highIncomeWealthy"),
            getMetric(user, "referrals"),
            getMetric(user, "closingInterview"),
            getMetric(user, "salesClose"),
          ].join(",");
        }
      }),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `${fileName}_${formatDateForInput(dateRange.startDate)}_to_${formatDateForInput(dateRange.endDate)}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="bm-user-management-table-container">
      <Paper elevation={2} sx={{ padding: "24px", borderRadius: "12px" }}>
        <Box className="management-controls-header">
          {selectedTeamLead ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<FiArrowLeft />}
                onClick={handleBackToTLs}
              >
                Back to Team Leads
              </Button>
              <Typography variant="h5" component="h1">
                Team Overview: {selectedTeamLead.name}
              </Typography>
            </Box>
          ) : (
            <Typography variant="h5" component="h1">
              Team Lead Overview
            </Typography>
          )}

          <Box className="filters-wrapper">
            <TextField
              variant="outlined"
              size="small"
              placeholder={`Search by ${
                selectedTeamLead ? "RM" : "TL"
              } Name...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FiSearch />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="From"
              type="date"
              size="small"
              sx={{ width: 180 }}
              value={formatDateForInput(dateRange.startDate)}
              onChange={(e) =>
                setDateRange((prev) => ({
                  ...prev,
                  startDate: new Date(e.target.value + "T00:00:00"),
                }))
              }
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="To"
              type="date"
              size="small"
              sx={{ width: 180 }}
              value={formatDateForInput(dateRange.endDate)}
              onChange={(e) =>
                setDateRange((prev) => ({
                  ...prev,
                  endDate: new Date(e.target.value + "T00:00:00"),
                }))
              }
              InputLabelProps={{ shrink: true }}
            />
            <Button
              variant="contained"
              size="small"
              startIcon={<FiDownload />}
              onClick={downloadCSV}
              disabled={isLoading || (selectedTeamLead ? relationshipManagers.length === 0 : teamLeads.length === 0)}
              sx={{ minWidth: 120 }}
            >
              Download CSV
            </Button>
          </Box>
        </Box>

        {isLoading ? (
          <Box display="flex" justifyContent="center" p={5}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <>
            {/* TEAM LEADS TABLE */}
            {!selectedTeamLead && teamLeads.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ mb: 2, color: "var(--primary-purple)" }}>
                  Team Leads
                </Typography>
                <TableContainer component={Paper} elevation={1}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Team Lead Name</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Zonal Head</TableCell>
                        <TableCell>Director</TableCell>
                        <TableCell align="right">Calls</TableCell>
                        <TableCell align="right">F2F</TableCell>
                        <TableCell align="right">Appt. Made</TableCell>
                        <TableCell align="right">Appt. Kept</TableCell>
                        <TableCell align="right">Fact Find</TableCell>
                        <TableCell align="right">Reviews</TableCell>
                        <TableCell align="right">Social</TableCell>
                        <TableCell align="right">HIW</TableCell>
                        <TableCell align="right">Referrals</TableCell>
                        <TableCell align="right">Closing Int.</TableCell>
                        <TableCell align="right">Sales Close</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {teamLeads.map((tl) => (
                        <TableRow
                          key={tl.userId}
                          hover
                          sx={{ cursor: "pointer" }}
                          onClick={() => setSelectedTeamLead(tl)}
                        >
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              color: "var(--primary-purple)",
                            }}
                          >
                            {tl.name}
                          </TableCell>
                          <TableCell>
                            {tl.leaveStatus ? (
                              <Tooltip
                                title={`On leave until ${formatNotificationDate(
                                  tl.leaveEndDate
                                )}`}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    color: "#D97706",
                                    fontWeight: "bold",
                                  }}
                                >
                                  <FiClock size={14} style={{ marginRight: 4 }} />
                                  On Leave
                                </Box>
                              </Tooltip>
                            ) : (
                              <Box sx={{ color: "#16A34A", fontWeight: "bold" }}>
                                Active
                              </Box>
                            )}
                          </TableCell>
                          <TableCell>{tl.zonalHeadName || "N/A"}</TableCell>
                          <TableCell>{tl.directorName || "N/A"}</TableCell>
                          <TableCell align="right">
                            {getMetric(tl, "telephoneSalesCalls")}
                          </TableCell>
                          <TableCell align="right">
                            {getMetric(tl, "faceToFace")}
                          </TableCell>
                          <TableCell align="right">
                            {getMetric(tl, "appointmentMade")}
                          </TableCell>
                          <TableCell align="right">
                            {getMetric(tl, "appointmentKept")}
                          </TableCell>
                          <TableCell align="right">
                            {getMetric(tl, "factFind")}
                          </TableCell>
                          <TableCell align="right">
                            {getMetric(tl, "serviceReviews")}
                          </TableCell>
                          <TableCell align="right">
                            {getMetric(tl, "socialCalls")}
                          </TableCell>
                          <TableCell align="right">
                            {getMetric(tl, "highIncomeWealthy")}
                          </TableCell>
                          <TableCell align="right">
                            {getMetric(tl, "referrals")}
                          </TableCell>
                          <TableCell align="right">
                            {getMetric(tl, "closingInterview")}
                          </TableCell>
                          <TableCell align="right">
                            {getMetric(tl, "salesClose")}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* DIRECT RELATIONSHIP MANAGERS TABLE */}
            {!selectedTeamLead && directRelationshipManagers.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ mb: 2, color: "#059669" }}>
                  Direct Relationship Managers
                </Typography>
                <TableContainer component={Paper} elevation={1}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>RM Name</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Branch Manager</TableCell>
                        <TableCell>Zonal Head</TableCell>
                        <TableCell>Director</TableCell>
                        <TableCell align="right">Calls</TableCell>
                        <TableCell align="right">F2F</TableCell>
                        <TableCell align="right">Appt. Made</TableCell>
                        <TableCell align="right">Appt. Kept</TableCell>
                        <TableCell align="right">Fact Find</TableCell>
                        <TableCell align="right">Reviews</TableCell>
                        <TableCell align="right">Social</TableCell>
                        <TableCell align="right">HIW</TableCell>
                        <TableCell align="right">Referrals</TableCell>
                        <TableCell align="right">Closing Int.</TableCell>
                        <TableCell align="right">Sales Close</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {directRelationshipManagers.map((rm) => (
                        <TableRow key={rm.userId} hover>
                          <TableCell sx={{ fontWeight: 600, color: "#059669" }}>
                            {rm.name}
                          </TableCell>
                          <TableCell>
                            {rm.leaveStatus ? (
                              <Tooltip
                                title={`On leave until ${formatNotificationDate(
                                  rm.leaveEndDate
                                )}`}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    color: "#D97706",
                                    fontWeight: "bold",
                                  }}
                                >
                                  <FiClock size={14} style={{ marginRight: 4 }} />
                                  On Leave
                                </Box>
                              </Tooltip>
                            ) : (
                              <Box sx={{ color: "#16A34A", fontWeight: "bold" }}>
                                Active
                              </Box>
                            )}
                          </TableCell>
                          <TableCell>{rm.branchManagerName || "N/A"}</TableCell>
                          <TableCell>{rm.zonalHeadName || "N/A"}</TableCell>
                          <TableCell>{rm.directorName || "N/A"}</TableCell>
                          <TableCell align="right">
                            {getMetric(rm, "telephoneSalesCalls")}
                          </TableCell>
                          <TableCell align="right">
                            {getMetric(rm, "faceToFace")}
                          </TableCell>
                          <TableCell align="right">
                            {getMetric(rm, "appointmentMade")}
                          </TableCell>
                          <TableCell align="right">
                            {getMetric(rm, "appointmentKept")}
                          </TableCell>
                          <TableCell align="right">
                            {getMetric(rm, "factFind")}
                          </TableCell>
                          <TableCell align="right">
                            {getMetric(rm, "serviceReviews")}
                          </TableCell>
                          <TableCell align="right">
                            {getMetric(rm, "socialCalls")}
                          </TableCell>
                          <TableCell align="right">
                            {getMetric(rm, "highIncomeWealthy")}
                          </TableCell>
                          <TableCell align="right">
                            {getMetric(rm, "referrals")}
                          </TableCell>
                          <TableCell align="right">
                            {getMetric(rm, "closingInterview")}
                          </TableCell>
                          <TableCell align="right">
                            {getMetric(rm, "salesClose")}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* RELATIONSHIP MANAGERS UNDER SELECTED TEAM LEAD */}
            {selectedTeamLead && (
              <Box sx={{ mt: 2 }}>
                <TableContainer component={Paper} elevation={1}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>RM Name</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Branch Manager</TableCell>
                        <TableCell>Zonal Head</TableCell>
                        <TableCell>Director</TableCell>
                        <TableCell align="right">Calls</TableCell>
                        <TableCell align="right">F2F</TableCell>
                        <TableCell align="right">Appt. Made</TableCell>
                        <TableCell align="right">Appt. Kept</TableCell>
                        <TableCell align="right">Fact Find</TableCell>
                        <TableCell align="right">Reviews</TableCell>
                        <TableCell align="right">Social</TableCell>
                        <TableCell align="right">HIW</TableCell>
                        <TableCell align="right">Referrals</TableCell>
                        <TableCell align="right">Closing Int.</TableCell>
                        <TableCell align="right">Sales Close</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {relationshipManagers.map((rm) => (
                        <TableRow key={rm.userId} hover>
                          <TableCell sx={{ fontWeight: 600 }}>
                            {rm.name}
                          </TableCell>
                          <TableCell>
                            {rm.leaveStatus ? (
                              <Tooltip
                                title={`On leave until ${formatNotificationDate(
                                  rm.leaveEndDate
                                )}`}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    color: "#D97706",
                                    fontWeight: "bold",
                                  }}
                                >
                                  <FiClock size={14} style={{ marginRight: 4 }} />
                                  On Leave
                                </Box>
                              </Tooltip>
                            ) : (
                              <Box sx={{ color: "#16A34A", fontWeight: "bold" }}>
                                Active
                              </Box>
                            )}
                          </TableCell>
                          <TableCell>{rm.branchManagerName || "N/A"}</TableCell>
                          <TableCell>{rm.zonalHeadName || "N/A"}</TableCell>
                          <TableCell>{rm.directorName || "N/A"}</TableCell>
                          <TableCell align="right">
                            {getMetric(rm, "telephoneSalesCalls")}
                          </TableCell>
                          <TableCell align="right">
                            {getMetric(rm, "faceToFace")}
                          </TableCell>
                          <TableCell align="right">
                            {getMetric(rm, "appointmentMade")}
                          </TableCell>
                          <TableCell align="right">
                            {getMetric(rm, "appointmentKept")}
                          </TableCell>
                          <TableCell align="right">
                            {getMetric(rm, "factFind")}
                          </TableCell>
                          <TableCell align="right">
                            {getMetric(rm, "serviceReviews")}
                          </TableCell>
                          <TableCell align="right">
                            {getMetric(rm, "socialCalls")}
                          </TableCell>
                          <TableCell align="right">
                            {getMetric(rm, "highIncomeWealthy")}
                          </TableCell>
                          <TableCell align="right">
                            {getMetric(rm, "referrals")}
                          </TableCell>
                          <TableCell align="right">
                            {getMetric(rm, "closingInterview")}
                          </TableCell>
                          <TableCell align="right">
                            {getMetric(rm, "salesClose")}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </>
        )}
      </Paper>
    </div>
  );
};

export default BranchManagerUserManagement;
